import { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Alert,
} from '@mui/material';
import { useSubscriptionPaymentStore } from '../../stores/root_store';

export const ManagerUserPayments = observer(() => {
  const { userKey } = useParams<{ userKey: string }>();
  const paymentStore = useSubscriptionPaymentStore();

  useEffect(() => {
    if (userKey) {
      paymentStore.fetchManagerPayments(userKey, true);
    }
    return () => paymentStore.clear();
  }, [userKey, paymentStore]);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTasksIncluded = (count: number, rollover: number) => {
    if (count === 99) return '\u221E'; // infinity symbol
    if (rollover > 0) {
      const base = count - rollover;
      return `${base} + ${rollover} rollover`;
    }
    return count.toString();
  };

  const handleLoadMore = () => {
    if (userKey) {
      paymentStore.fetchManagerPayments(userKey, false);
    }
  };

  if (paymentStore.loading && paymentStore.allPayments.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Subscription Payment History
        </Typography>

        {paymentStore.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {paymentStore.error}
          </Alert>
        )}

        {paymentStore.allPayments.length === 0 ? (
          <Typography color="text.secondary">No payments found.</Typography>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Tasks Included</TableCell>
                    <TableCell>Tasks Used</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Expires</TableCell>
                    <TableCell>Plan</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paymentStore.allPayments.map((payment) => (
                    <TableRow key={payment.key}>
                      <TableCell>
                        {formatTasksIncluded(
                          payment.tasks_included,
                          payment.rollover
                        )}
                      </TableCell>
                      <TableCell>{payment.tasks_used}</TableCell>
                      <TableCell>{formatDate(payment.date_created)}</TableCell>
                      <TableCell>{formatDate(payment.tasks_expire)}</TableCell>
                      <TableCell>{payment.product_id || '-'}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {payment.gift && (
                            <Chip label="Gift" size="small" color="secondary" />
                          )}
                          {payment.refunded && (
                            <Chip label="Refunded" size="small" color="error" />
                          )}
                          {!payment.gift && !payment.refunded && payment.success && (
                            <Chip label="Paid" size="small" color="success" />
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {paymentStore.hasMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                  onClick={handleLoadMore}
                  disabled={paymentStore.loading}
                  variant="outlined"
                >
                  {paymentStore.loading ? (
                    <CircularProgress size={20} />
                  ) : (
                    'Load More'
                  )}
                </Button>
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
});
